/**
 * Lifecycle owner for the RNNoise publish path.
 *
 * Same generation guard as the video controller: loading the WASM is async, and
 * a user who double-taps "noise suppression" must not end up with two
 * AudioContexts holding the microphone open.
 */
import type { AudioProcessingPreferences, DenoiseRuntime } from '../settings/preferences';
import { applyNativeProcessing } from './audioConstraints';
import { createAudioPipeline, fallbackRuntime, type AudioPipeline } from './audioPipeline';

export interface DenoiseController {
  /** The processed track, or null when the raw mic track should be published. */
  readonly track: MediaStreamTrack | null;
  readonly runtime: DenoiseRuntime;
  apply(source: MediaStream | null, preferences: AudioProcessingPreferences): Promise<void>;
  stop(): void;
}

export function createDenoiseController(onChange: () => void): DenoiseController {
  let pipeline: AudioPipeline | null = null;
  let runtime: DenoiseRuntime = 'off';
  let generation = 0;

  const setRuntime = (next: DenoiseRuntime): void => {
    if (next === runtime) return;
    runtime = next;
    onChange();
  };

  const teardown = (): void => {
    pipeline?.stop();
    pipeline = null;
  };

  return {
    get track() {
      return pipeline?.track ?? null;
    },
    get runtime() {
      return runtime;
    },

    async apply(source, preferences) {
      generation += 1;
      const mine = generation;
      teardown();

      const raw = source?.getAudioTracks()[0] ?? null;
      if (!source || !raw) {
        setRuntime('off');
        return;
      }

      if (preferences.noiseSuppression !== 'rnnoise') {
        // Native flags are the only processing in this mode; push them now so a
        // toggle takes effect without a getUserMedia round trip.
        await applyNativeProcessing(raw, preferences, preferences.noiseSuppression === 'native');
        if (mine === generation) setRuntime(fallbackRuntime(preferences));
        return;
      }

      const created = await createAudioPipeline(source, preferences);
      if (mine !== generation) {
        created?.stop();
        return;
      }
      if (!created) {
        await applyNativeProcessing(raw, preferences, true);
        setRuntime('unavailable');
        return;
      }
      pipeline = created;
      setRuntime(created.runtime);
    },

    stop() {
      generation += 1;
      teardown();
      setRuntime('off');
    },
  };
}
